import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from '@rsos/base-components';
import InputText from '@rsos/components/capstone/base/form/inputs/InputText';
import { addConversation } from '@rsos/reduxDataStore/src/features/conversations/conversationsSlice';
import { DebugPanel } from '../DebugPanel';
import useLocationPicker from '../LocationDebugPanel/useLocationPicker';
import { sendLocation } from '../services/els';
import {
  StyledDescription,
  StyledError,
  StyledHint,
  StyledInputWrapper,
  StyledLocationInfo,
  StyledSuccess,
  StyledWarning,
} from './TextTo911DebugPanel.styles';

/**
 * Delay in ms before re-injecting conversation state. When the call is
 * selected, `useGetConversationMetaData` fires a real API call that 404s
 * (no backend session) and the `matchRejected` handler deletes our entry.
 * Re-dispatching after a delay ensures the conversation state persists.
 */
const REINJECT_DELAY_MS = 3000;

type Status = 'idle' | 'sending' | 'sent' | 'error';

interface TextTo911DebugPanelControlsProps {
  phoneNumber: string;
}

function TextTo911DebugPanelControls({
  phoneNumber,
}: TextTo911DebugPanelControlsProps) {
  const map = window.__RSOS?.map;
  const location = useLocationPicker(map);
  const dispatch = useDispatch();
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  // See REINJECT_DELAY_MS — we need to re-dispatch addConversation after
  // the metadata fetch (triggered on call selection) 404s and deletes our
  // injected entry from Redux.
  const reinjectTimerRef = useRef<ReturnType<typeof setTimeout>>();

  async function handleSimulate() {
    if (!location) return;

    setStatus('sending');
    setErrorMessage('');

    if (reinjectTimerRef.current) {
      clearTimeout(reinjectTimerRef.current);
    }

    const conversationId = `tcapi.${crypto.randomUUID()}`;
    const now = Date.now();

    const conversationPayload = {
      caller_id: phoneNumber,
      source: 'tcapi',
      conversationId,
      callerId: phoneNumber,
      status: 'pending' as const,
      acceptStatus: 'pending' as const,
      sessionType: 'tcapi' as const,
      creationTime: now,
      lastUpdatedTime: now,
      serverTimeMillis: now,
      authorityAccountId: '',
      authorityDisplayName: '',
      twilioPhone: null,
      translationMethod: null,
      lastCallerLanguage: null,
      expireTime: null,
      lastAcceptTime: null,
      sessionId: conversationId,
    };

    try {
      await sendLocation({
        latitude: location.latitude,
        longitude: location.longitude,
        phoneNumber,
        source: 'tcapi',
      });

      dispatch(addConversation(conversationPayload));

      // Re-inject after the metadata fetch (triggered on call selection)
      // fails and deletes our conversation entry.
      reinjectTimerRef.current = setTimeout(() => {
        dispatch(addConversation(conversationPayload));
      }, REINJECT_DELAY_MS);

      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setErrorMessage(
        err instanceof Error ? err.message : 'Unknown error occurred',
      );
    }
  }

  if (!location) {
    return (
      <StyledHint>
        <strong>Click the map</strong> to select a location inside the
        jurisdiction.
      </StyledHint>
    );
  }

  return (
    <div>
      <StyledLocationInfo>
        <div>
          <strong>Latitude:</strong> {location.latitude}
        </div>
        <div>
          <strong>Longitude:</strong> {location.longitude}
        </div>
      </StyledLocationInfo>
      <Button
        onClick={handleSimulate}
        btnType="primary"
        disabled={status === 'sending'}
      >
        <strong>
          {status === 'sending' ? 'Sending...' : 'Simulate Text-to-911 Session'}
        </strong>
      </Button>
      {status === 'sent' && (
        <StyledSuccess>
          Text-to-911 session simulated. The call should appear in the queue.
        </StyledSuccess>
      )}
      {status === 'error' && <StyledError>Error: {errorMessage}</StyledError>}
    </div>
  );
}

interface TextTo911DebugPanelProps {
  onClose?: () => void;
}

function TextTo911DebugPanel({ onClose }: TextTo911DebugPanelProps) {
  const [phoneNumber, setPhoneNumber] = useState('15555555555');

  return (
    <DebugPanel title="Simulate Text-to-911 Session" onClose={onClose}>
      <StyledDescription>
        Simulates an inbound Text-to-911 call by posting a location to ELS with{' '}
        <code>source: &quot;tcapi&quot;</code> and injecting conversation state
        into Redux.
      </StyledDescription>
      <StyledWarning>
        &quot;Accept Chat&quot; and &quot;End Chat&quot; buttons will not work —
        there is no real backend session. The authority must have the TT911
        capability enabled.
      </StyledWarning>
      <StyledInputWrapper>
        <InputText
          type="text"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setPhoneNumber(event.target.value)
          }
          value={phoneNumber}
        />
      </StyledInputWrapper>
      <TextTo911DebugPanelControls phoneNumber={phoneNumber} />
    </DebugPanel>
  );
}

export default TextTo911DebugPanel;
